import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { qbankApi, paperApi, assistantApi, setsApi, roman, gapTone } from '../lib/questionBankApi';
import type {
  BankHealthView, BlueprintView, NamedOption, PaperView, PaperQuestionView,
  GapListView, GapView, DraftBatchView, SetPlanView,
} from '../lib/questionBankApi';
import { subjectLabel } from '../lib/questionBankApi';
import { kindLabel, sourcesApi } from '../lib/sourcesApi';
import type { SourceSummary } from '../lib/sourcesApi';
import { StatTile } from '../components/charts';
import { useTenant } from '../context/TenantContext';
import { storedFileSrc } from '../lib/api';

/**
 * THE PAPER BUILDER.
 *
 * ⚠ THE ORDER OF THIS SCREEN IS THE ARGUMENT. The health verdict comes FIRST, above the paper,
 * because "can I set this paper at all" is the question a head of department actually has and
 * nobody has ever been able to answer it in September. Building the paper is the easy half.
 *
 * ⚠ THE COVERAGE TABLE IS NOT DECORATION EITHER. A paper that satisfies every section count and
 * misses the board's 50/20/30 typology split is still the wrong paper, and the split cuts across
 * the sections so a human counting questions cannot see it. Three lines of table are the only
 * place in this product where that is visible.
 */
export function PaperBuilderPage() {
  const [institutions, setInstitutions] = useState<NamedOption[]>([]);
  const [institutionId, setInstitutionId] = useState<string | null>(null);
  const [blueprints, setBlueprints] = useState<BlueprintView[]>([]);
  const [subjects, setSubjects] = useState<NamedOption[]>([]);

  /**
   * ⚠ WHETHER THE TOP BAR HAS ALREADY PINNED A CAMPUS. Read once at render rather than watched: the
   * campus picker reloads the page when it changes, so there is nothing to subscribe to.
   */
  const campusPinned = (() => {
    try { const c = localStorage.getItem('eduvora.campus'); return !!c && c !== 'all'; }
    catch { return false; }
  })();

  const [blueprintId, setBlueprintId] = useState<string>('');
  const [subjectId, setSubjectId] = useState<string>('');
  const [setLabel, setSetLabel] = useState('Set 1');
  // ⚠ Blank means "use the blueprint's own value" — the placeholder shows what that will be, so an
  // empty box never looks like a field somebody forgot to fill in.
  const [title, setTitle] = useState('');
  const [classLabel, setClassLabel] = useState('');
  const [paperDate, setPaperDate] = useState('');

  /**
   * What the title box will fall back to if it is left blank — shown as the placeholder so an empty
   * field never reads as one somebody forgot. Mirrors PaperReader: `Title` when set, otherwise
   * "<subject> — <blueprint>".
   */
  const defaultTitle = `${subjects.find((x) => x.id === subjectId)?.name ?? 'Subject'} — ${blueprints.find((b) => b.id === blueprintId)?.name ?? 'blueprint'}`;

  const [health, setHealth] = useState<BankHealthView | null>(null);
  const [paper, setPaper] = useState<PaperView | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'' | 'build' | 'save'>('');
  const [failed, setFailed] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const [gaps, setGaps] = useState<GapListView | null>(null);
  const [batch, setBatch] = useState<DraftBatchView | null>(null);
  const [drafting, setDrafting] = useState<string | null>(null);

  const [library, setLibrary] = useState<SourceSummary[] | null>(null);
  const [libraryFailed, setLibraryFailed] = useState(false);
  /** ⚠ Empty by default, and it stays that way until a teacher ticks something. Defaulting to
      "all the chapters" would silently change what the assistant is asked to do and would cost
      money on every draft; the two modes are a choice, not a setting to inherit. */
  const [sourceIds, setSourceIds] = useState<string[]>([]);
  /** The chapters the batch on screen was actually written from — snapshotted at the moment of the
      request. ⚠ Not the current ticks: a teacher who unticks a chapter after drafting must not have
      the citations under the drafts quietly stop resolving, or start resolving to something else. */
  const [batchSources, setBatchSources] = useState<SourceSummary[]>([]);

  const [sets, setSets] = useState(3);
  const [mode, setMode] = useState<'Shuffled' | 'Parallel'>('Shuffled');
  const [withReserve, setWithReserve] = useState(false);
  const [setPlan, setSetPlan] = useState<SetPlanView | null>(null);
  const [building, setBuilding] = useState(false);

  // ------------------------------------------------------------------ bootstrap
  useEffect(() => {
    let alive = true;
    qbankApi
      .list({ take: '1', ...(institutionId ? { institutionId } : {}) })
      .then((r) => {
        if (!alive) return;
        setInstitutions(r.institutions);
        setBlueprints(r.blueprints);
        setSubjects(r.subjects);
        if (!institutionId && r.institutionId) setInstitutionId(r.institutionId);
        setFailed(false);
      })
      .catch(() => alive && setFailed(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [institutionId]);

  // The first blueprint, and the subject it is for. ⚠ A blueprint carries its own subject in this
  // product; picking a mismatched pair is the fastest way to get an empty paper and blame the
  // software, so the subject follows the blueprint rather than being chosen independently.
  useEffect(() => {
    if (blueprintId || blueprints.length === 0) return;
    const first = blueprints[0];
    setBlueprintId(first.id);
    if (first.subjectId) setSubjectId(first.subjectId);
  }, [blueprints, blueprintId]);

  const blueprint = useMemo(
    () => blueprints.find((b) => b.id === blueprintId) ?? null,
    [blueprints, blueprintId],
  );

  const audit = useMemo(() => (paper ? auditAnswers(paper) : null), [paper]);

  const pickBlueprint = (id: string) => {
    setBlueprintId(id);
    setPaper(null);
    const b = blueprints.find((x) => x.id === id);
    if (b?.subjectId) setSubjectId(b.subjectId);
  };

  // ------------------------------------------------------------------ health
  useEffect(() => {
    if (!blueprintId || !subjectId) return;
    let alive = true;
    qbankApi
      .health(blueprintId, subjectId)
      .then((h) => alive && setHealth(h))
      .catch(() => alive && setHealth(null));

    // ⚠ Reading the gaps costs nothing — it is the same arithmetic the health panel already did.
    // DRAFTING costs money, and only happens when somebody presses a button.
    assistantApi
      .gaps(blueprintId, subjectId)
      .then((g) => alive && setGaps(g))
      .catch(() => alive && setGaps(null));

    setBatch(null);
    return () => { alive = false; };
  }, [blueprintId, subjectId]);

  // ------------------------------------------------------------------ the library
  // ⚠ Scoped to the subject as well as the school. A Chemistry chapter cannot answer a Physics gap,
  // and a picker listing every chapter the trust owns is a list nobody reads.
  useEffect(() => {
    if (!subjectId) return;
    let alive = true;
    sourcesApi
      .list(institutionId, subjectId)
      .then((r) => {
        if (!alive) return;
        setLibrary(r);
        setLibraryFailed(false);
        // ⚠ Drop ticks for chapters that are no longer listed. Changing subject otherwise leaves an
        // invisible selection behind and the next draft is written from a chapter not on screen.
        setSourceIds((ids) => ids.filter((id) => r.some((s) => s.id === id)));
      })
      .catch(() => { if (alive) { setLibrary(null); setLibraryFailed(true); } });
    return () => { alive = false; };
  }, [institutionId, subjectId]);

  const toggleSource = (id: string) =>
    setSourceIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));

  const chosenSources = useMemo(
    () => (library ?? []).filter((s) => sourceIds.includes(s.id)),
    [library, sourceIds],
  );

  const askAssistant = (g: GapView, save: boolean) => {
    setDrafting(g.label);
    setBatch(null);
    setFlash(null);
    setBatchSources(chosenSources);
    assistantApi
      .draft({
        blueprintId, subjectId, sectionLabel: g.sectionLabel, marks: g.marks,
        type: g.type, typology: g.typology, difficulty: g.difficulty,
        unitId: g.unitId, count: g.count, save,
        // ⚠ Omitted, not sent empty, when nothing is ticked — that is the switch between the two
        // modes on the server.
        ...(sourceIds.length > 0 ? { sourceIds } : {}),
      })
      .then((b) => {
        setBatch(b);
        if (save && b.savedIds.length > 0) {
          setFlash(`${plural(b.savedIds.length, 'draft')} added to the bank. `
            + `${b.savedIds.length === 1 ? 'It is a draft' : 'They are drafts'} — somebody has `
            + 'to read and approve each one before it can go on a paper.');
          // Re-read: the health panel counts drafts separately, and that number just changed.
          qbankApi.health(blueprintId, subjectId).then(setHealth).catch(() => {});
          assistantApi.gaps(blueprintId, subjectId).then(setGaps).catch(() => {});
        }
      })
      .catch(() => setFlash('The assistant could not be reached.'))
      .finally(() => setDrafting(null));
  };

  const build = useCallback(() => {
    if (!blueprintId || !subjectId) return;
    setBusy('build');
    setFlash(null);
    paperApi
      .preview({ blueprintId, subjectId, setLabel, title: title.trim() || null, classLabel: classLabel.trim() || null, paperDate: paperDate || null })
      .then((p) => setPaper(p))
      .catch(() => setFlash('Could not build the paper.'))
      .finally(() => setBusy(''));
  }, [blueprintId, subjectId, setLabel]);

  const buildSets = (save: boolean) => {
    if (!blueprintId || !subjectId) return;
    setBuilding(true);
    setFlash(null);
    setsApi
      .build({ blueprintId, subjectId, sets, mode, withReserve, save })
      .then((p) => {
        setSetPlan(p);
        if (save) setFlash(`${plural(p.sets.length, 'set')} saved as drafts.`);
      })
      .catch(() => setFlash('Could not build the sets.'))
      .finally(() => setBuilding(false));
  };

  const save = () => {
    if (!blueprintId || !subjectId) return;
    setBusy('save');
    paperApi
      .save({ blueprintId, subjectId, setLabel, title: title.trim() || null, classLabel: classLabel.trim() || null, paperDate: paperDate || null })
      .then(() => setFlash('Saved as a draft. It stays in "Papers" until you finalise it.'))
      .catch(() => setFlash('Could not save the paper.'))
      .finally(() => setBusy(''));
  };

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Opening the question bank…</p>;
  if (failed) {
    return <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>Could not load the question bank.</div>;
  }

  const verdict = health?.verdict ?? '';
  const blocked = verdict === 'CannotBuild';

  return (
    <div className="qb">
      {/* Everything above the sheet is hidden when the paper is printed — see qbank.css. */}
      <div className="qb-screen">
        <header className="qb-head">
          <h1>Paper builder</h1>
          <p className="qb-sub">
            Assembled to the board's section structure, its typology split and its unit weightage —
            all three at once, from the questions you already have.
          </p>
        </header>

        <div className="card qb-controls">
          {/*
            ⚠ HIDDEN WHEN THE TOP BAR HAS ALREADY ANSWERED THIS. Two controls on one screen that set
            the same thing is how somebody picks a campus in one of them, reads a figure produced by
            the other, and cannot tell which. The campus is a property of the SESSION — it lives in
            the top bar — so this page-level control only appears for a caller who has NOT chosen
            there, which is the "all campuses" case where a paper still has to belong to one.
          */}
          {institutions.length > 1 && !campusPinned && (
            <label>
              <span>Institution</span>
              <select value={institutionId ?? ''} onChange={(e) => { setInstitutionId(e.target.value); setPaper(null); }}>
                {institutions.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
              </select>
            </label>
          )}

          <label className="qb-wide">
            <span>Blueprint</span>
            <select value={blueprintId} onChange={(e) => pickBlueprint(e.target.value)}>
              {blueprints.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </label>

          <label>
            <span>Subject</span>
            {/*
              ⚠ THE CODE IS SHOWN, AND IT IS NOT DECORATION. A school runs "Art & Craft" for every
              class it teaches — separate Subject rows, distinct codes, IDENTICAL NAMES. Rendering
              only the name produced eleven consecutive lines reading "Art & Craft" and the
              reasonable conclusion that the data was duplicated. It was not; the control was hiding
              the only thing that told them apart. The server has always sent the code.
            */}
            <select value={subjectId} onChange={(e) => { setSubjectId(e.target.value); setPaper(null); }}>
              {subjects.map((s) => (
                <option key={s.id} value={s.id}>{subjectLabel(s, subjects)}</option>
              ))}
            </select>
          </label>

          {/*
            ═══ WHAT PRINTS AT THE TOP OF THE PAPER ═══

            ⚠ THESE FOUR WERE ALREADY IN THE REQUEST AND ONLY ONE WAS ON THE SCREEN. BuildBody has
            carried title, classLabel, setLabel and paperDate since it was written; the form offered
            Set and nothing else, so the heading, the class line and the date could not be changed
            from the one place a person builds a paper. They fell back to the blueprint's defaults
            and looked like something the software had decided.

            ⚠ AND THE REST OF THE HEADING IS DELIBERATELY NOT HERE. Time allowed, maximum marks, the
            general instructions and the section structure belong to the BLUEPRINT, because they are
            the board's pattern and are meant to be identical across every paper built from it —
            editing them per paper is how two sections of the same exam end up with different
            durations. They are edited in the blueprint editor. The institution name on the
            letterhead comes from the institution record, for the same reason a letterhead does.
          */}
          <label>
            <span>Paper title</span>
            <input value={title} placeholder={defaultTitle}
              onChange={(e) => setTitle(e.target.value)} />
          </label>

          <label>
            <span>Class</span>
            <input value={classLabel} placeholder="from the blueprint"
              onChange={(e) => setClassLabel(e.target.value)} />
          </label>

          <label>
            <span>Set</span>
            <input value={setLabel} onChange={(e) => setSetLabel(e.target.value)} />
          </label>

          <label>
            <span>Date</span>
            {/* ⚠ type="date" renders MM/DD/YYYY whatever the locale — a known and reported flaw.
                It is left as a date input because a free-text date on an exam paper is worse. */}
            <input type="date" value={paperDate} onChange={(e) => setPaperDate(e.target.value)} />
          </label>

          <div className="qb-actions">
            <button className="btn btn-primary" onClick={build} disabled={busy !== '' || !blueprintId}>
              {busy === 'build' ? 'Building…' : 'Build the paper'}
            </button>
            {/* ⚠ No bare "Print" here any more. A single Print button prints whatever happens to be
                on screen, which is exactly how a marking scheme gets handed to a class. The two
                named actions live above the sheet instead — see the answer key panel below. */}
            {paper && (
              <button className="btn" onClick={save} disabled={busy !== ''}>
                {busy === 'save' ? 'Saving…' : 'Save as draft'}
              </button>
            )}
          </div>
        </div>

        {flash && <div className="qb-flash">{flash}</div>}

        {/* ------------------------------------------------------------ health */}
        {/* ⚠ A PATTERN CARRIES ITS OWN SUBJECT AND THE PICKERS DO NOT HAVE TO AGREE.
            Choosing a blueprint sets the subject to its own, but the subject can then be
            changed independently — and a Mathematics pattern paired with Computer Science
            silently asks the bank for questions that were never going to exist. The health
            panel then reports an empty bank, which is true and is not the reason. */}
        {blueprint?.subjectId && subjectId && blueprint.subjectId !== subjectId && (
          <div className="card" style={{ padding: 12, marginBottom: 12, borderLeft: '3px solid var(--warn)' }}>
            <strong style={{ fontSize: 13 }}>This pattern is not for this subject.</strong>{' '}
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              <em>{blueprint.name}</em> was written for a different subject. The section structure
              and mark split will still be applied, but the questions are being looked for in{' '}
              <strong>{subjects.find((x) => x.id === subjectId)?.name ?? 'this subject'}</strong> —
              so an empty bank here may mean the pairing is wrong rather than that questions are missing.
            </span>
          </div>
        )}

        {health && (
          <div className={`card qb-health verdict-${verdict.toLowerCase()}`}>
            <div className="qb-health-head">
              <span className={`qb-verdict v-${verdict.toLowerCase()}`}>
                {verdict === 'Ready' ? 'Ready' : verdict === 'Thin' ? 'Thin' : 'Cannot build'}
              </span>
              <p>{health.headline}</p>
            </div>

            <div className="qb-tiles">
              <StatTile label="Approved" value={String(health.approvedTotal)} foot="usable on a paper" />
              <StatTile label="In draft" value={String(health.draftTotal)}
                foot={health.draftTotal > 0 ? 'waiting for a second pair of eyes' : 'nothing waiting'} />
              <StatTile label="Never used" value={String(health.neverUsed)} foot="fresh questions in the bank" />
              <StatTile label="Retired" value={String(health.retiredTotal)} foot="kept, not deleted" />
            </div>

            {health.actions.length > 0 && (
              <ul className="qb-actions-list">
                {health.actions.map((a, i) => (
                  <li key={i} className={a.urgent ? 'is-urgent' : ''}>{a.text}</li>
                ))}
              </ul>
            )}

            <div className="qb-supply">
              <table className="qb-table">
                <thead>
                  <tr><th>Section</th><th className="num">Needs</th><th className="num">Approved</th>
                    <th className="num">Draft</th><th>Verdict</th></tr>
                </thead>
                <tbody>
                  {health.sections.map((s) => (
                    <tr key={s.label} className={s.shortfall > 0 ? 'is-short' : ''}>
                      <td><strong>{s.label}</strong> · {s.title}{' '}
                        <span className="muted">({plural(s.marksEach, 'mark')})</span></td>
                      <td className="num">{s.demand}</td>
                      <td className="num">{s.approved}</td>
                      <td className="num">{s.draft || '—'}</td>
                      <td>
                        {s.shortfall === 0
                          ? <span className="tone-ok">covered</span>
                          : s.draftsWouldFix
                            ? <span className="tone-warn">short {s.shortfall} — the drafts would fix it</span>
                            : <span className="tone-bad">short {s.shortfall}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ------------------------------------------------------------ the assistant */}
        {gaps && gaps.gaps.length > 0 && (
          <div className="card qb-assist">
            <div className="qb-assist-head">
              <h2>Ask the assistant to write what is missing</h2>
              <p className="qb-sub">
                Not “generate some questions” — each button below is one specific hole in the bank:
                the section, the unit, the mark value and the kind of thinking the board wants. Every
                draft arrives as a <strong>draft</strong> and a person has to approve it before the
                paper builder will touch it.
              </p>
            </div>

            <SourcePicker
              library={library}
              failed={libraryFailed}
              selected={sourceIds}
              onToggle={toggleSource}
              onClear={() => setSourceIds([])}
            />

            {/* ⚠ ABOVE THE BUTTONS, NOT UNDER THE RESULTS. The two modes produce materially
                different questions, and a teacher who works out which one ran by noticing that
                nothing is cited has already spent the money and read the wrong questions. */}
            <div className={`qb-mode ${chosenSources.length > 0 ? 'is-sourced' : 'is-open'}`}>
              <span className="qb-mode-tag">
                {chosenSources.length > 0 ? 'From the book' : 'From the model'}
              </span>
              {chosenSources.length > 0 ? (
                <p>
                  The assistant will write{' '}
                  <strong>only from the {plural(chosenSources.length, 'chapter')}</strong> ticked
                  above, and name the page it used. Anything those chapters do not support it is
                  told to leave out — so expect fewer questions than you asked for rather than
                  invented material.
                </p>
              ) : (
                <p>
                  Nothing is ticked, so the assistant writes{' '}
                  <strong>from the model’s own knowledge</strong>. Right for the syllabus, but tied
                  to no page: the drafts will carry no citation and nobody can check them against a
                  book.
                </p>
              )}
            </div>

            <div className="qb-gaps">
              {gaps.gaps.map((g, i) => (
                <div className="qb-gap" key={i}>
                  <div className="qb-gap-main">
                    <span className="qb-gap-label">{g.label}</span>
                    <span className="qb-gap-reason">{g.reason}</span>
                    <div className="qb-chips">
                      <span className={`qb-chip typ-${g.typology.toLowerCase()}`}>
                        {g.typology === 'Competency' ? 'Competency-based'
                          : g.typology === 'Objective' ? 'Objective' : 'Short and long answer'}
                      </span>
                      <span className="qb-chip">{g.difficulty}</span>
                      <span className="qb-chip">Section {g.sectionLabel}</span>
                    </div>
                  </div>
                  <div className="qb-gap-actions">
                    <button className="btn" disabled={drafting !== null}
                      onClick={() => askAssistant(g, false)}>
                      {drafting === g.label ? 'Writing…' : 'Draft'}
                    </button>
                    <button className="btn btn-primary" disabled={drafting !== null}
                      onClick={() => askAssistant(g, true)}>
                      Draft and keep
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {batch && <DraftBatch batch={batch} sources={batchSources} />}

        {/* ------------------------------------------------------------ sets */}
        <div className="card qb-sets">
          <h2>More than one set of this paper</h2>
          <p className="qb-sub">
            Three different things get called “multiple sets”, and they are not the same job.
            <strong> Shuffled</strong> is what a board's Set 1 / Set 2 / Set 3 actually is — the same
            questions in a different order with the options permuted, so nobody can read their
            neighbour's paper. It is equivalent by construction and costs the bank nothing.
            <strong> Parallel</strong> is a genuinely different paper for the same blueprint, which
            is what you want as a spare if one gets out — and it eats the bank several times over.
            An <em>easier</em> or <em>harder</em> version is neither: two sets of one exam must be
            equally hard or the marks cannot be compared, so difficulty belongs on a practice
            blueprint instead.
          </p>

          <div className="qb-setctl">
            <label>
              <span>Mode</span>
              <select value={mode} onChange={(e) => { setMode(e.target.value as 'Shuffled' | 'Parallel'); setSetPlan(null); }}>
                <option value="Shuffled">Shuffled — same questions, different order</option>
                <option value="Parallel">Parallel — different questions</option>
              </select>
            </label>
            <label>
              <span>How many</span>
              <select value={sets} onChange={(e) => { setSets(Number(e.target.value)); setSetPlan(null); }}>
                {[2, 3, 4].map((n) => <option key={n} value={n}>{n} sets</option>)}
              </select>
            </label>
            <label className="qb-check">
              <input type="checkbox" checked={withReserve}
                onChange={(e) => { setWithReserve(e.target.checked); setSetPlan(null); }} />
              <span>Plus a sealed reserve</span>
            </label>
            <div className="qb-actions">
              <button className="btn" disabled={building || !blueprintId} onClick={() => buildSets(false)}>
                {building ? 'Building…' : 'Build the sets'}
              </button>
              {setPlan && (
                <button className="btn btn-primary" disabled={building} onClick={() => buildSets(true)}>
                  Save all as drafts
                </button>
              )}
            </div>
          </div>

          {withReserve && (
            <p className="qb-sub">
              ⚠ The reserve is held back and its questions are <strong>not</strong> marked as used
              when the main paper is finalised — so it stays fresh until the day you need it.
            </p>
          )}

          {setPlan && <SetResult plan={setPlan} />}
        </div>

        {blocked && !paper && (
          <div className="card qb-blocked">
            <p><strong>Nothing to print yet.</strong> You can still build the paper to see exactly
              which slots come out empty — that list is the fastest way to know what to write.</p>
          </div>
        )}

        {/* ------------------------------------------------------------ coverage */}
        {paper && (
          <div className="card qb-coverage">
            <h2>Where this paper landed</h2>
            <p className="qb-sub">
              Marks against the blueprint. The section counts are exact by construction; these three
              are the targets a paper set by hand cannot be checked against.
            </p>

            <div className="qb-cov-grid">
              <Coverage title="Typology — the board's rule" rows={paper.typology} />
              <Coverage title="Difficulty — the house rule" rows={paper.difficulty} />
              <Coverage title="Units — the board's weightage" rows={paper.units} showTarget={!paper.unitWeightsMissing} />
            </div>

            {paper.note && <p className="qb-note">{paper.note}</p>}
            {paper.repeats > 0 && (
              <p className="qb-note">
                {plural(paper.repeats, 'question')} {paper.repeats === 1 ? 'has' : 'have'} appeared
                on a recent paper.
              </p>
            )}
          </div>
        )}

        {paper && paper.unfilled.length > 0 && (
          <div className="card qb-unfilled">
            <h2>Slots that could not be filled</h2>
            {/* ⚠ Grouped by reason. Four identical sentences, one per empty slot, is the same
                information printed four times and it buries the one line that differs. */}
            <ul>
              {groupUnfilled(paper.unfilled).map((g, i) => (
                <li key={i}>
                  <strong>
                    Section {g.sectionLabel}, question{g.numbers.length > 1 ? 's' : ''}{' '}
                    {listOf(g.numbers)}
                  </strong>{' '}
                  ({g.marks} mark{g.marks > 1 ? 's' : ''} each)
                  <span> — {g.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* --------------------------------------------------- the answer key, above the sheet */}
        {paper && audit && (
          <div className="card qb-key">
            <div className="qb-key-head">
              <h2>Answer key <span className="qb-key-alt">(marking scheme)</span></h2>
              <p className={`qb-key-count ${audit.missing.length > 0 ? 'is-short' : ''}`}>
                {audit.missing.length === 0
                  ? `All ${audit.total} ${audit.hasParts ? 'questions and sub-parts' : 'questions'} have an answer recorded.`
                  : `${audit.recorded} of ${audit.total} ${audit.hasParts ? 'questions and sub-parts' : 'questions'} have an answer recorded.`}
              </p>
            </div>

            {audit.missing.length > 0 && (
              <p className="qb-note">
                ⚠ The key will print <strong>“No answer recorded”</strong> against{' '}
                {audit.missing.length === 1 ? 'question ' : 'questions '}
                {listOf(audit.missing.slice(0, MissingShown))}
                {audit.missing.length > MissingShown
                  && ` and ${audit.missing.length - MissingShown} more`}.
                {' '}An examiner marking from an incomplete key marks by guesswork — put the
                answers in the bank and build again.
              </p>
            )}

            <div className="qb-key-actions">
              <button className="btn btn-primary" onClick={() => printOnly('paper')}>
                Print question paper
              </button>
              <button className="btn btn-accent" onClick={() => printOnly('key')}>
                Print answer key
              </button>
              <button className="btn" aria-pressed={showKey} onClick={() => setShowKey(!showKey)}>
                {showKey ? 'Hide the key on screen' : 'Preview the key on screen'}
              </button>
            </div>

            <p className="qb-sub">
              Two buttons because they are two different documents.{' '}
              <strong>Print question paper</strong> never carries the key, whether or not the
              preview below is open. The key prints on its own pages, headed for the examiner.
            </p>
          </div>
        )}
      </div>

      {/* ================================================================ the sheet */}
      {paper && <PaperSheet paper={paper} blueprint={blueprint} showKey={showKey} />}
    </div>
  );
}

// ---------------------------------------------------------------------- the sets

/**
 * ⚠ THE EQUIVALENCE TABLE IS THE POINT, NOT THE LIST OF SETS.
 *
 * Two parallel sets both totalling eighty marks look identical to any school comparing totals, and
 * one of them can carry six more marks of competency-based work than the other — the part children
 * find hardest. Nobody has ever been able to see that before the paper was sat. Shuffled sets are
 * equal by construction and the table says so instead of inventing a measurement.
 */
const EquivalenceRowsShown = 8;

function SetResult({ plan }: { plan: SetPlanView }) {
  const short = plan.sets.filter((s) => s.unfilled > 0);
  const hidden = Math.max(0, plan.equivalence.length - EquivalenceRowsShown);
  const widestHidden = hidden > 0 ? plan.equivalence[EquivalenceRowsShown].spread : 0;
  // ⚠ A set with empty slots is left out of the comparison on purpose — comparing a 28-mark
  // reserve against an 80-mark paper reports a 52-mark spread that says nothing about Set 1 and
  // Set 2. But a missing column looks exactly like a column that was checked and found equal.
  const excluded = plan.sets.filter((s) => !plan.setLabels.includes(s.label));

  return (
    <div className="qb-setresult">
      <div className="qb-setgrid">
        {plan.sets.map((s) => (
          <div className={`qb-setcard ${s.unfilled > 0 ? 'is-short' : ''} ${s.isReserve ? 'is-reserve' : ''}`}
            key={s.label}>
            <span className="qb-setlabel">
              {s.label}
              {s.isReserve && <em> · sealed</em>}
            </span>
            <span className="qb-setnums">{s.questions} questions · {s.marks} marks</span>
            {s.unfilled > 0
              ? <span className="tone-bad">{s.unfilled} slot{s.unfilled > 1 ? 's' : ''} empty</span>
              : <span className="tone-ok">complete</span>}
            {/* ⚠ The one line that says what is actually different about this set. Without it
                three cards read as three copies of one paper. */}
            {s.unfilled === 0 && (
              <span className="qb-setperm">
                {plan.mode === 'Parallel'
                  ? 'no question shared with the other sets'
                  : s.optionsPermuted > 0
                    ? `options re-ordered on ${s.optionsPermuted} question${s.optionsPermuted > 1 ? 's' : ''}`
                    : 'printed in the bank’s own order'}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className={`qb-verdictline ${plan.equivalent ? 'is-ok' : 'is-bad'}`}>
        {plan.verdict}
      </div>

      {plan.equivalence.length > 0 && (
        <div className="qb-supply">
          <table className="qb-table">
            <thead>
              <tr>
                <th>Axis</th><th>Line</th>
                {plan.setLabels.map((l) => <th key={l} className="num">{l}</th>)}
                <th className="num">Spread</th>
              </tr>
            </thead>
            <tbody>
              {plan.equivalence.slice(0, EquivalenceRowsShown).map((l, i) => (
                <tr key={i} className={l.spread > 4 ? 'is-short' : ''}>
                  <td className="muted">{l.axis}</td>
                  <td>{l.name}</td>
                  {l.marksPerSet.map((m, k) => <td key={k} className="num">{m}</td>)}
                  <td className={`num ${l.spread > 4 ? 'tone-bad' : l.spread > 0 ? 'tone-warn' : 'tone-ok'}`}>
                    {l.spread}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* ⚠ Say what was cut. The rows are widest-first, so nothing actionable is below the
              fold — but a table that stops without saying so reads as the whole comparison. */}
          {hidden > 0 && (
            <p className="qb-sub">
              The {plan.equivalence.length} lines are ordered widest first;{' '}
              {hidden} further line{hidden > 1 ? 's are' : ' is'} not shown, none of them wider
              than {widestHidden} mark{widestHidden === 1 ? '' : 's'}.
            </p>
          )}

          {excluded.length > 0 && (
            <p className="qb-sub">
              {excluded.map((s) => s.label).join(' and ')}{' '}
              {excluded.length > 1 ? 'are' : 'is'} <strong>not in this comparison</strong> — a set
              with empty slots is not a paper yet, and measuring it against a complete one reports
              a difference that is only the missing questions.
            </p>
          )}
        </div>
      )}

      {plan.note && <p className="qb-note">{plan.note}</p>}
      {short.length > 0 && (
        <p className="qb-sub">
          A set with empty slots is not printable. The empty slots name exactly what is missing —
          the assistant panel above can write it.
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- what came back

/**
 * ⚠ THE REJECTED LIST IS SHOWN, NOT HIDDEN, AND IT IS THE MOST IMPORTANT PART OF THIS PANEL.
 *
 * "The assistant wrote six, four were kept, two were rejected — one duplicated a question already
 * in the bank and one had two correct options." A tool that silently discards a third of its own
 * output gives a teacher no way to calibrate how far to trust the rest, and the first time they
 * find a bad question that got through, they stop using the whole feature.
 */
function DraftBatch({ batch, sources }: { batch: DraftBatchView; sources: SourceSummary[] }) {
  const sourced = sources.length > 0;
  return (
    <div className="card qb-batch">
      <div className="qb-batch-head">
        <h2>{batch.gapLabel}</h2>
        <span className="qb-batch-count">
          asked for {batch.asked} · {batch.returned} came back · {batch.drafts.length} kept
          {batch.rejected.length > 0 ? ` · ${batch.rejected.length} rejected` : ''}
        </span>
      </div>

      <p className="qb-batch-mode">
        {sourced
          ? `Written from ${plural(sources.length, 'chapter')}: ${sources.map((s) => s.title).join(', ')}.`
          : 'Written from the model’s own knowledge — no chapter was chosen, so nothing here cites a page.'}
      </p>

      {/* ⚠ A SHORT BATCH IS THE FEATURE, NOT A FAULT, and it has to read that way or the next
          teacher ticks nothing and gets the full count of unsourced questions instead. */}
      {batch.returned < batch.asked && (
        <p className={`qb-shortfall ${sourced ? '' : 'is-plain'}`}>
          <strong>{batch.returned} of the {batch.asked} asked for.</strong>{' '}
          {sourced
            ? 'Working only from the chapters, the assistant stops when the material runs out '
              + 'instead of inventing what the book does not contain. Tick another chapter, or '
              + 'write the rest by hand.'
            : 'The assistant returned fewer than it was asked for. Ask again for the remainder, or '
              + 'narrow the gap it is being asked to fill.'}
        </p>
      )}

      {batch.note && <p className="qb-note">{batch.note}</p>}

      {batch.savedIds.length === 0 && batch.drafts.length > 0 && (
        <p className="qb-sub">
          Nothing has been saved. Press <strong>Draft and keep</strong> to put these in the bank as
          drafts.
        </p>
      )}

      {batch.drafts.map((d, i) => (
        <div className="qb-draft" key={i}>
          <div className="qb-draft-head">
            <span className="qb-marks">{d.marks}</span>
            <p className="qb-q-text">{d.text}</p>
          </div>

          {d.options.length > 0 && (
            <ol className="qb-options">
              {d.options.map((o) => (
                <li key={o.label} className={o.isCorrect ? 'is-correct' : ''}>
                  ({o.label.toLowerCase()}) {o.text}
                </li>
              ))}
            </ol>
          )}

          {d.parts.length > 0 && (
            // ⚠ Numbered (i), (ii) — the same way the sheet prints sub-parts. A draft that reads
            // "1., 2." here and "(i), (ii)" once placed looks like a different question.
            <ol className="qb-parts">
              {d.parts.map((p, j) => (
                <li key={j}>
                  <span className="qb-part-label">({roman(j + 1)})</span>{' '}
                  <span className="qb-part-marks">{p.marks}</span> {p.text}
                  {p.answer && <em className="qb-ans"> {p.answer}</em>}
                </li>
              ))}
            </ol>
          )}

          {d.answer && <p className="qb-ans"><strong>Answer.</strong> {d.answer}</p>}

          <div className="qb-chips">
            <span className="qb-chip ai">Drafted by the assistant</span>
            <span className="qb-chip">{d.typeLabel}</span>
            <span className="qb-chip">{d.difficulty}</span>
            <span className="qb-chip">{d.bloom}</span>
          </div>

          <Citation citation={d.citation} sources={sources} />
        </div>
      ))}

      {batch.rejected.length > 0 && (
        <div className="qb-rejected">
          <h3>Rejected before you saw them</h3>
          <ul>
            {batch.rejected.map((r, i) => (
              <li key={i}>
                <span className="qb-rej-reason">{r.reason}</span>
                <span className="qb-rej-text">{r.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- the source picker

/**
 * WHICH CHAPTERS THE ASSISTANT MAY WRITE FROM.
 *
 * ⚠ The school's own material and the platform's shared library are listed apart because they are
 * not the same claim. "NCERT Chapter 9, page 32" is checkable by anyone holding the prescribed
 * book; "Ms Kamath's revision notes, page 7" is checkable only by this school — and a head of
 * department deciding whether to trust a paper needs to know which of the two they are looking at
 * before they tick it, not after a parent asks.
 */
function SourcePicker({ library, failed, selected, onToggle, onClear }: {
  library: SourceSummary[] | null;
  failed: boolean;
  selected: string[];
  onToggle: (id: string) => void;
  onClear: () => void;
}) {
  if (failed) {
    return (
      <p className="qb-sub">
        Could not read the source library, so drafting from a chapter is not available at the
        moment. The assistant can still write from the model’s own knowledge.
      </p>
    );
  }
  if (library === null) return <p className="qb-sub">Looking for chapters…</p>;

  // ⚠ An empty box with three ticks-worth of chrome round it reads as "this school has nothing
  // worth using". Say where the chapters come from instead.
  if (library.length === 0) {
    return (
      <div className="qb-src-empty">
        <strong>No chapters in the library for this subject yet.</strong>
        <p>
          Add the chapter you are teaching from on <Link to="/sources">Source material</Link> and
          the assistant can write questions out of it, each one naming the page it used. Until then
          it writes from the model’s own knowledge.
        </p>
      </div>
    );
  }

  const groups = [
    {
      key: 'mine',
      title: 'This school’s own material',
      hint: 'uploaded here — chapters, handouts and notes your teachers put in',
      items: library.filter((s) => !s.shared),
    },
    {
      key: 'shared',
      title: 'Eduvora’s shared library',
      hint: 'the prescribed books, the same copy every school reads',
      items: library.filter((s) => s.shared),
    },
  ].filter((g) => g.items.length > 0);

  return (
    <div className="qb-srcpick">
      <div className="qb-srcpick-head">
        <h3>Write from a chapter</h3>
        <span className="qb-srcpick-count">
          {selected.length === 0 ? 'none chosen' : `${plural(selected.length, 'chapter')} chosen`}
          {selected.length > 0 && (
            <>
              {' · '}
              <button type="button" className="qb-linkbtn" onClick={onClear}>clear</button>
            </>
          )}
        </span>
      </div>

      {groups.map((g) => (
        <div className="qb-srcgroup" key={g.key}>
          <p className="qb-srcgroup-head">
            <strong>{g.title}</strong> <span className="qb-sub">— {g.hint}</span>
          </p>
          <div className="qb-srclist">
            {g.items.map((s) => {
              const on = selected.includes(s.id);
              return (
                <label className={`qb-srcopt ${on ? 'is-on' : ''} ${s.shared ? 'is-shared' : ''}`} key={s.id}>
                  <input type="checkbox" checked={on} onChange={() => onToggle(s.id)} />
                  <span className="qb-srcopt-body">
                    <span className="qb-srcopt-title">{s.title}</span>
                    {s.attribution && <span className="qb-srcopt-attr">{s.attribution}</span>}
                    <span className="qb-chips">
                      <span className="qb-chip">{kindLabel(s.kind)}</span>
                      {s.unitName && <span className="qb-chip unit">{s.unitName}</span>}
                      {s.classLabel && <span className="qb-chip">Class {s.classLabel}</span>}
                      <span className="qb-chip">{plural(s.pageCount, 'page')}</span>
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------- the citation

/** A citation resolved back to something the reader can open. `page` is null when the string names
    a chapter but no page; `beyondEnd` when it names a page the chapter does not have. */
interface CitationHit { source: SourceSummary; page: number | null; beyondEnd: boolean }

/**
 * ⚠ THE CITATION COMES BACK AS PROSE, NOT AS AN ID, so it has to be matched to a document by its
 * words. Anything cleverer would be guessing: the model is free to write "NCERT XII Physics, p.
 * 104", "Ray Optics and Optical Instruments, page 104" or "Ch. 9, p. 104" for the same page.
 *
 * The match is deliberately narrow. Only the chapters THIS batch was written from are candidates —
 * the model was given nothing else, so a citation that matches nothing in that set is a citation
 * that cannot be trusted rather than one to go hunting for in the wider library. Distinct words of
 * three letters or more are scored against each candidate's title, attribution, subject and unit;
 * pure numbers count too (they are chapter numbers) except the one already read as the page. Two
 * words must agree, or one when there is only a single candidate to confuse it with.
 *
 * ⚠ WHEN IT CANNOT BE RESOLVED NOTHING IS OPENED AND NOTHING IS GUESSED. A tie between two
 * candidates, no match at all, or a page number the chapter does not have all end the same way:
 * the citation is shown as plain text with the reason it could not be opened, and the teacher is
 * told to check the book by hand. Opening the "probably right" chapter would be worse than
 * useless — a teacher who reads a page that does not support the question cannot tell whether the
 * assistant lied or the resolver did.
 */
function resolveCitation(citation: string, sources: SourceSummary[]): CitationHit | null {
  const page = pageFromCitation(citation);
  const words = new Set(citationTokens(citation, page));

  let best: SourceSummary | null = null;
  let bestScore = 0;
  let tied = false;
  for (const s of sources) {
    const own = new Set(citationTokens(
      `${s.title} ${s.attribution ?? ''} ${s.subjectName ?? ''} ${s.unitName ?? ''} ${s.classLabel ?? ''}`,
      page,
    ));
    let score = 0;
    for (const t of own) if (words.has(t)) score += 1;
    if (score > bestScore) { best = s; bestScore = score; tied = false; }
    else if (score === bestScore && score > 0) tied = true;
  }

  const enough = bestScore >= 2 || (bestScore >= 1 && sources.length === 1);
  let chosen = best !== null && enough && !tied ? best : null;
  // The single-candidate case: the assistant was handed exactly one chapter, so whatever words it
  // chose, that is the only chapter it can have read.
  if (chosen === null && sources.length === 1) chosen = sources[0];
  if (chosen === null) return null;

  return { source: chosen, page, beyondEnd: page !== null && page > chosen.pageCount };
}

/** "p. 104", "pp. 104–106" (the first page of the range), "page 104". */
function pageFromCitation(citation: string): number | null {
  const m = /\b(?:pp?\.?|pages?)\s*(\d{1,4})\b/i.exec(citation);
  return m ? Number(m[1]) : null;
}

/** ⚠ The page number itself is dropped: "p. 32" must not match a chapter that happens to be 32
    pages long or to be Chapter 32. Other numbers stay — they are how "Chapter 9" tells itself
    apart from "Chapter 10" when every other word in the two titles is identical. */
function citationTokens(text: string, page: number | null): string[] {
  const stop = new Set(['the', 'and', 'for', 'from', 'with', 'class', 'part', 'page', 'pages',
    'chapter', 'unit', 'ncert', 'rationalised', 'edition']);
  return text
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 0)
    .filter((t) => (/^\d+$/.test(t) ? page === null || Number(t) !== page : t.length >= 3))
    .filter((t) => !stop.has(t));
}

/**
 * ⚠ THIS IS THE WHOLE ARGUMENT FOR THE FEATURE. A teacher who can open the page in ten seconds
 * trusts the draft; one who cannot, does not — and a citation nobody can check is worth less than
 * no citation at all, because it looks like evidence.
 */
function Citation({ citation, sources }: { citation: string | null; sources: SourceSummary[] }) {
  const [open, setOpen] = useState(false);
  const hit = useMemo(
    () => (citation && sources.length > 0 ? resolveCitation(citation, sources) : null),
    [citation, sources],
  );

  // No chapter was chosen: nothing was cited and nothing should have been. Said once, quietly,
  // rather than left as an empty space the teacher has to interpret.
  if (sources.length === 0) {
    return (
      <p className="qb-cite is-own">
        {/* ⚠ A citation in this mode is not a source. The assistant was handed nothing, so anything
            it names is the model's recollection of a book rather than the book, and it must not be
            offered as something to open. */}
        {citation
          ? `It names “${citation}” — but no chapter was given to it, so that is the model’s recollection of a book, not the book.`
          : 'From the model’s own knowledge — no chapter, no page to check it against.'}
      </p>
    );
  }

  // ⚠ THE INTERESTING FAILURE. Chapters were handed over and the assistant still would not say
  // which page it used, so this draft is the model's own work wearing the book's clothes.
  if (!citation) {
    return (
      <p className="qb-cite is-missing">
        <strong>No page cited.</strong> The chapters were given to the assistant, but it did not say
        where this one came from — treat it as its own work and read it against the chapter before
        you keep it.
      </p>
    );
  }

  if (!hit) {
    return (
      <p className="qb-cite is-unresolved">
        <span className="qb-cite-mark">Cited</span> {citation}
        <span className="qb-cite-why">
          {' — '}Eduvora cannot tell which of the chosen chapters this names, so it cannot open it
          for you. Check it against the book by hand.
        </span>
      </p>
    );
  }

  const openable = hit.page !== null && !hit.beyondEnd;

  return (
    <div className={`qb-cite is-cited ${openable ? '' : 'is-partial'}`}>
      <button type="button" className="qb-cite-btn" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span className="qb-cite-mark">Cited</span>
        <span className="qb-cite-line">{citation}</span>
        <span className="qb-cite-cta">{open ? 'Hide the page' : 'Check the page'}</span>
      </button>

      {hit.page === null && (
        <p className="qb-cite-why">
          No page number in that citation — it opens {hit.source.title} at the first page and the
          rest is a hunt.
        </p>
      )}
      {hit.beyondEnd && (
        <p className="qb-cite-why is-bad">
          ⚠ It cites page {hit.page}, and {hit.source.title} has only{' '}
          {plural(hit.source.pageCount, 'page')}. The page it names does not exist, so read the
          question with that in mind.
        </p>
      )}

      {open && (
        <CitedPage
          source={hit.source}
          page={openable ? (hit.page as number) : 1}
        />
      )}
    </div>
  );
}

/**
 * The stored text of one page, straight out of the library — the same text the assistant was given,
 * not the original PDF, so what a teacher reads here is what it read.
 */
function CitedPage({ source, page }: { source: SourceSummary; page: number }) {
  const [at, setAt] = useState(page);
  const [cache, setCache] = useState<Record<number, string>>({});
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setBusy(true);
    sourcesApi
      .pages(source.id, at, at)
      .then((r) => {
        setFailed(false);
        // ⚠ Recorded even when the server returns nothing for it, so a genuinely empty page is not
        // re-fetched every time it is looked at — and so "empty" can be told from "still loading".
        setCache((c) => ({ ...c, [at]: r.pages.find((p) => p.page === at)?.text ?? '' }));
      })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
    /* ⚠ No `alive` flag, for the reason spelled out in the sources reader: StrictMode mounts this
       twice and a cancelled first response would leave the panel on "Fetching…" for ever. The
       component is still mounted either way, and the result is keyed by page number, so a late
       response can only fill a cache slot — never overwrite the page being read. */
  }, [source.id, at]);

  const text = cache[at];

  return (
    <div className="qb-cite-reader">
      <div className="qb-cite-nav">
        <button className="btn btn-quiet" disabled={at <= 1} onClick={() => setAt(at - 1)}>Previous</button>
        <span className="qb-cite-at">
          {source.title} · page {at} of {source.pageCount}
          {at !== page && <em> · not the cited page</em>}
        </span>
        <button className="btn btn-quiet" disabled={at >= source.pageCount} onClick={() => setAt(at + 1)}>Next</button>
      </div>

      {failed ? (
        <p className="qb-sub">Could not fetch that page.</p>
      ) : text === undefined ? (
        <p className="qb-sub">{busy ? 'Fetching…' : ''}</p>
      ) : text.trim().length === 0 ? (
        <p className="qb-sub">
          ⚠ Nothing is stored for page {at}. The citation points at a page the library has no text
          for, so this question cannot be checked against it.
        </p>
      ) : (
        <p className="qb-cite-page">{text}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- coverage table

function Coverage({ title, rows, showTarget = true }: {
  title: string;
  rows: { name: string; targetMarks: number; actualMarks: number; gap: number }[];
  showTarget?: boolean;
}) {
  return (
    <div className="qb-cov">
      <h3>{title}</h3>
      <table className="qb-table">
        <thead>
          <tr><th>Line</th>{showTarget && <th className="num">Target</th>}<th className="num">Paper</th>{showTarget && <th className="num">Gap</th>}</tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.name}>
              <td>{r.name}</td>
              {showTarget && <td className="num">{r.targetMarks}</td>}
              <td className="num">{r.actualMarks}</td>
              {showTarget && (
                <td className={`num tone-${gapTone(r.gap)}`}>
                  {r.gap === 0 ? '—' : r.gap > 0 ? `+${r.gap}` : r.gap}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!showTarget && (
        <p className="qb-sub">No unit weightage is set, so there is nothing to compare against.</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------- the printable sheet

/**
 * ⚠ THIS IS THE ONLY PART THAT PRINTS. Everything else on the page is hidden by the print
 * stylesheet. A "print preview" that shows the app's chrome is the reason schools export to Word
 * and retype the paper, which defeats the whole feature.
 */
function PaperSheet({ paper, blueprint, showKey }: {
  paper: PaperView; blueprint: BlueprintView | null; showKey: boolean;
}) {
  // ⚠ MAXIMUM MARKS IS WHAT IS ON THE SHEET, NEVER WHAT THE BLUEPRINT ASKED FOR. A paper handed
  // to a child that misstates its own total is unmarkable: 70 printed over 59 marks of questions
  // means every script is out of a number that does not exist, and the first person to notice is
  // a fifteen-year-old halfway through the exam. When the two differ we print the real total and
  // say why underneath, because a total that is quietly correct still hides a broken paper.
  const shortBy = paper.totalMarks - paper.marksPlaced;
  const numbers = printedNumbers(paper);

  return (
    <>
      <div className="paper-sheet">
        <header className="ps-head">
          <Letterhead name={paper.institutionName} />
          {paper.title && <p className="ps-title">{paper.title}</p>}
          <h1>{paper.subjectName}</h1>
          <p className="ps-meta">
            {[paper.classLabel, paper.setLabel, formatPaperDate(paper.paperDate)]
              .filter(Boolean).join(' · ')}
          </p>
          <div className="ps-bar">
            <span>Time allowed: {formatDuration(paper.durationMinutes)}</span>
            <span>Maximum marks: {paper.marksPlaced}</span>
          </div>
          {shortBy > 0 && (
            <p className="ps-short">
              This paper is incomplete: {plural(paper.unfilled.length, 'slot')} could not be filled,
              so it carries {paper.marksPlaced} marks and not the {paper.totalMarks} of the
              blueprint. Do not sit it until the missing questions are set.
            </p>
          )}
          {paper.generalInstructions && (
            <div className="ps-instructions">
              <strong>General instructions</strong>
              <p>{paper.generalInstructions}</p>
            </div>
          )}
        </header>

        {paper.sections.map((s) => (
          <section className="ps-section" key={s.label}>
            <h2>Section {s.label} — {s.title}</h2>
            {s.instruction && <p className="ps-sec-instr">{s.instruction}</p>}

            {s.questions.map((qn, i) => (
              <Printed key={`${qn.questionId}-${i}`} q={qn} marksEach={s.marksEach}
                number={numbers.get(questionKey(s.label, i)) ?? qn.number} />
            ))}
          </section>
        ))}
      </div>

      {/* ⚠ ALWAYS IN THE DOM, never conditionally mounted. `window.print()` is synchronous and
          reads the page as it stands, so a React state change cannot get the key on paper in time —
          see printOnly(). Visibility is CSS only: hidden on screen unless previewed, hidden on
          paper unless the "Print answer key" button asked for it. */}
      <div className={`paper-sheet key-sheet ${showKey ? 'is-shown' : ''}`}>
          <header className="ps-head">
            <Letterhead name={paper.institutionName} />
            {paper.title && <p className="ps-title">{paper.title}</p>}
            {/* "Marking scheme" is what the board calls it and what an examiner expects to read;
                "answer key" is what the teacher looking for it calls it. Lead with theirs. */}
            <h1>{paper.subjectName} — answer key <span className="ps-h1-alt">(marking scheme)</span></h1>
            <p className="ps-meta">
              {[paper.classLabel, paper.setLabel, formatPaperDate(paper.paperDate)]
                .filter(Boolean).join(' · ')} ·
              {' '}for the use of the examiner only
            </p>
          </header>

          {paper.sections.map((s) => (
            <section className="ps-section" key={s.label}>
              <h2>Section {s.label}</h2>
              {s.questions.map((qn, i) => {
                // ⚠ The same numbering as the sheet, out of the same map — a key that numbers its
                // answers differently from the paper it marks is worse than no key at all.
                const n = numbers.get(questionKey(s.label, i)) ?? qn.number;
                return (
                <div className="ps-key" key={`${qn.questionId}-k-${i}`}>
                  {/* ⚠ A case study carries no answer of its own — the passage is not a question.
                      Printing "no answer recorded" against every case study made the marking
                      scheme look broken; the sub-parts below are the answer. */}
                  <p>
                    <strong>{qn.isAlternative ? `${n} (OR)` : n}.</strong>{' '}
                    {/* ⚠ hasAnswer, not `?? `: an answer stored as an empty string prints as a
                        blank line an examiner reads as "no marks here", and the count above the
                        sheet must agree with what actually lands on the page. */}
                    {hasAnswer(qn.answerKey)
                      ? qn.answerKey
                      : qn.parts.length > 0
                        ? <span className="ps-key-stem">see the sub-parts below</span>
                        : <em>No answer recorded for this question.</em>}
                    <span className="ps-key-marks">{qn.marks}</span>
                  </p>
                  {qn.parts.map((p, j) => (
                    <p className="ps-key-part" key={p.questionId}>
                      ({roman(j + 1)}){' '}
                      {hasAnswer(p.answerKey) ? p.answerKey : <em>No answer recorded.</em>}
                      <span className="ps-key-marks">{p.marks}</span>
                    </p>
                  ))}
                </div>
                );
              })}
            </section>
          ))}
      </div>

      {blueprint && (
        <p className="ps-footnote">
          Built to “{blueprint.name}”: {blueprint.competencyPercent}% competency-based,{' '}
          {blueprint.objectivePercent}% objective, {blueprint.descriptivePercent}% short and long
          answer.
        </p>
      )}
    </>
  );
}

function Printed({ q, marksEach, number }: {
  q: PaperQuestionView; marksEach: number; number: number;
}) {
  return (
    <div className={`ps-q ${q.isAlternative ? 'is-or' : ''}`}>
      {/* ⚠ "OR" on its own line above the alternative, exactly as a board paper prints it. A
          teacher who has to work out which two questions are a pair will not use this again. */}
      {q.isAlternative && <p className="ps-or">OR</p>}

      <div className="ps-q-row">
        <span className="ps-num">{q.isAlternative ? '' : `${number}.`}</span>
        <div className="ps-q-body">
          <p className="ps-text">{q.text}</p>

          {q.options.length > 0 && (
            <ol className="ps-options">
              {q.options.map((o) => (
                <li key={o.label}><span className="ps-opt-label">({o.label.toLowerCase()})</span> {o.text}</li>
              ))}
            </ol>
          )}

          {q.parts.length > 0 && (
            <ol className="ps-parts">
              {q.parts.map((p, i) => (
                <li key={p.questionId}>
                  <span className="ps-opt-label">({roman(i + 1)})</span> {p.text}
                  <span className="ps-part-marks">{p.marks}</span>
                </li>
              ))}
            </ol>
          )}

          {q.hasDiagram && <p className="ps-diagram">[ diagram ]</p>}
        </div>
        <span className="ps-marks">{q.marks || marksEach}</span>
      </div>

      {/* ⚠ On screen only. The compromise note is for the person setting the paper, never for the
          child sitting it — see the print stylesheet, which removes it. */}
      {q.note && <p className="ps-note">{q.note}</p>}
    </div>
  );
}

/**
 * ⚠ THE PRINTED NUMBERS ARE NOT THE BLUEPRINT'S SLOT NUMBERS, AND ON A SHORT PAPER THEY CANNOT BE.
 *
 * When a slot cannot be filled the builder leaves its number behind, and the sheet printed
 * 23 → 26 with nothing in between. A child reads that as two questions they cannot find, spends
 * the first five minutes hunting for them and the invigilator has no answer. So the questions
 * that are actually on the sheet are numbered consecutively from one.
 *
 * Renumbering rather than printing "24–25 not set": the sheet is the child's document and an
 * empty slot is not their business — the shortfall is stated once, in the header, in the one
 * place that stops the paper being sat by mistake. The blueprint's own slot numbers stay on
 * screen in "Slots that could not be filled", which is where the person who has to fill them
 * needs them. The marking scheme is numbered from this same map so the two can never disagree.
 */
/**
 * ⚠ KEYED BY POSITION, NOT BY itemId — AND THAT IS A BUG FIX, NOT A STYLE CHOICE.
 *
 * This map used to be keyed by `q.itemId`. On a SAVED paper every item has its own row id and that
 * works. On a PREVIEW nothing is saved, so PaperReader deliberately passes `Guid.Empty` as the item
 * id for every question — correct, because there is no row to swap and the screen hides the swap
 * control when the id is empty.
 *
 * The consequence was that the map collapsed to a SINGLE entry whose value was the last number
 * assigned, and every lookup returned it. A paper of 10 + 5 + 3 non-alternative questions printed
 * "18." against all twenty-one — and so did the marking scheme, and so did the shortfall warning,
 * which read "questions 18, 18, 18, 18, 18 and 18 and 5 more".
 *
 * ⚠ AND IT WAS INVISIBLE IN THE ONE PLACE ANYONE WOULD LOOK. The totals, the coverage, the READY
 * verdict and the section table were all correct, because none of them go through this map. Only
 * the document a child actually sits was wrong.
 *
 * A section label plus an index is unique by construction and cannot be empty, so a preview and a
 * saved paper now number identically.
 */
export function questionKey(sectionLabel: string, index: number): string {
  return `${sectionLabel}#${index}`;
}

function printedNumbers(paper: PaperView): Map<string, number> {
  const map = new Map<string, number>();
  let n = 0;
  for (const s of paper.sections) {
    s.questions.forEach((q, i) => {
      // An "OR" alternative is not a new question — it carries the number of the one above it.
      if (!q.isAlternative) n += 1;
      map.set(questionKey(s.label, i), n);
    });
  }
  return map;
}

/**
 * ⚠ PRINTING IS SYNCHRONOUS AND REACT IS NOT.
 *
 * `window.print()` freezes the page and photographs the DOM as it stands at the instant it is
 * called. A `setState` in the same click handler has not been committed yet, so "set the flag then
 * print" prints the PREVIOUS state — which for the question-paper button means printing a paper
 * with the answer key still attached, i.e. the one failure that matters here. The switch is
 * therefore a class written straight onto <body>, before the call and off again in `finally`, so
 * what comes out of the printer always matches the button that was pressed even if the print
 * dialogue is cancelled or throws. The key sheet is always in the DOM for the same reason — see
 * PaperSheet.
 */
function printOnly(target: 'paper' | 'key'): void {
  const cls = target === 'key' ? 'printing-key' : 'printing-paper';
  document.body.classList.add(cls);
  try {
    window.print();
  } finally {
    document.body.classList.remove(cls);
  }
}

/** How many missing answers are named before the sentence stops being readable. */
const MissingShown = 6;

interface AnswerAudit {
  /** Lines the key will print an answer on: one per question, or one per sub-part. */
  total: number;
  recorded: number;
  /** Whether any question on this paper has sub-parts — decides what the count is called. */
  hasParts: boolean;
  /** Printed numbers of the lines that will read "No answer recorded", in sheet order. */
  missing: string[];
}

/**
 * ⚠ COUNTED THE WAY THE KEY PRINTS, NOT THE WAY THE PAPER READS.
 *
 * A case study is ONE question on the sheet and four lines in the key, and its stem carries no
 * answer of its own — the sub-parts are the answer. So a case study with answers on two of its four
 * parts is two answers and two holes, never "one question, answered". Counting the stem instead
 * reports a marking scheme as complete when half of it is missing, which is the single mistake this
 * count exists to catch.
 *
 * ⚠ One level deep, deliberately. The key renders one level of sub-parts, so a hole any deeper
 * never reaches the printed page and warning about it would send somebody hunting for nothing.
 */
function auditAnswers(paper: PaperView): AnswerAudit {
  const numbers = printedNumbers(paper);
  const missing: string[] = [];
  let total = 0;
  let recorded = 0;
  let hasParts = false;

  for (const s of paper.sections) {
    // ⚠ Same positional key as the sheet. This is the third consumer of the map and it produced the
    // warning that read "questions 18, 18, 18, 18, 18 and 18 and 5 more" — an unreadable message
    // about a real problem, which is worse than no message, because it looks like the warning is
    // broken rather than the paper.
    s.questions.forEach((q, qi) => {
      const n = numbers.get(questionKey(s.label, qi)) ?? q.number;
      const label = q.isAlternative ? `${n} (OR)` : String(n);
      if (q.parts.length > 0) {
        hasParts = true;
        q.parts.forEach((p, i) => {
          total += 1;
          if (hasAnswer(p.answerKey)) recorded += 1;
          else missing.push(`${label} (${roman(i + 1)})`);
        });
      } else {
        total += 1;
        if (hasAnswer(q.answerKey)) recorded += 1;
        else missing.push(label);
      }
    });
  }

  return { total, recorded, hasParts, missing };
}

/** ⚠ Blank is not an answer: "" and "   " print as an empty line the examiner cannot mark from. */
function hasAnswer(key: string | null): boolean {
  return key !== null && key.trim() !== '';
}

/** "1 mark", "2 marks" — an English plural, not the "(s)" of a form letter. */
function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * "12 January 2026". ⚠ Parsed by hand, not by `new Date`: a bare "2026-01-12" is read as UTC
 * midnight, so west of Greenwich the paper would print the day before the exam.
 */
function formatPaperDate(date: string | null): string {
  if (!date) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  if (!m) return date;
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const month = months[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : date;
}

interface UnfilledGroup { sectionLabel: string; marks: number; reason: string; numbers: number[] }

function groupUnfilled(rows: { sectionLabel: string; number: number; marks: number; reason: string }[]): UnfilledGroup[] {
  const map = new Map<string, UnfilledGroup>();
  for (const r of rows) {
    const key = `${r.sectionLabel}|${r.marks}|${r.reason}`;
    const g = map.get(key);
    if (g) g.numbers.push(r.number);
    else map.set(key, { sectionLabel: r.sectionLabel, marks: r.marks, reason: r.reason, numbers: [r.number] });
  }
  return [...map.values()];
}

/** "3", "3 and 4", "3, 4 and 5" — read aloud, not printed as an array. */
function listOf(ns: (number | string)[]): string {
  if (ns.length === 1) return String(ns[0]);
  return `${ns.slice(0, -1).join(', ')} and ${ns[ns.length - 1]}`;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} minutes`;
  if (m === 0) return h === 1 ? '1 hour' : `${h} hours`;
  return `${h} h ${m} min`;
}


/**
 * ⚠ apiUrl() IS ONLY CORRECT FOR A RELATIVE PATH, and a logo is not always one.
 *
 * A stored logo is usually "/api/files/…", which needs the API origin in front of it. It can also
 * already be absolute — an R2 public URL, a CDN, or a data: URI — and prefixing one of those
 * produces "https://api.eduvoratech.com" + "https://…", which 404s. The <img> then fires onError,
 * the crest hides itself, and the paper prints without it while everything else looks fine. That
 * is exactly what happened here and it took a DOM probe to find, because the symptom of a hidden
 * crest is indistinguishable from a school that never uploaded one.
 *
 * ⚠ `imgSrc` in BrandingPage.tsx has the same shape and the same hole. It has not bitten yet
 * because every logo so far has been a relative path.
 */
function logoSrc(url: string): string {
  return storedFileSrc(url)!;   // ⚠ one shared implementation — see lib/api.ts
}

/**
 * The school's own crest and name at the top of the paper.
 *
 * ⚠ THIS IS NOT DECORATION. A question paper carrying Eduvora's typography and the school's name in
 * plain text reads as something a supplier produced. The same sheet with the school's crest on it
 * reads as the school's own document, which is what a principal is actually buying. The branding is
 * already stored per tenant and was already on every other screen; the printed paper was the one
 * place it never reached.
 *
 * ⚠ The crest is hidden rather than broken if it fails to load. A missing image icon in the middle
 * of a letterhead, on a paper handed to three hundred children, is worse than no crest at all.
 */
function Letterhead({ name }: { name?: string | null }) {
  const { branding } = useTenant();
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = branding?.logoUrl && !logoFailed ? logoSrc(branding.logoUrl) : null;
  const school = name || branding?.name;

  if (!logo && !school) return null;

  return (
    <div className="ps-letterhead">
      {logo && (
        <img className="ps-crest" src={logo} alt="" onError={() => setLogoFailed(true)} />
      )}
      {school && <p className="ps-school">{school}</p>}
    </div>
  );
}
